import { SimulationTrackerActions } from 'src/app/state/simulation-tracker/simulation-tracker.actions';
import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { APP_CONFIG, AppConfig } from '../../appconfig/appconfig';
import { CLIENT_CONFIG, ClientConfig } from '../../appconfig/client-config';
import { Store } from '@ngxs/store';
import { NotificationsActions } from '../../state/notifications/notifications.actions';
import { Severity } from '../../global/enums/severity';
import { SimulationTrackerStatus } from '../enums/simulation-tracker-status';
import { SimulationTracker } from '../interfaces/simulation-tracker';
import { tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SignalRSimulationService {
  private store: Store = inject(Store);
  private authService: MsalService = inject(MsalService);
  private appConfig: AppConfig = inject(APP_CONFIG);
  private clientConfig: ClientConfig = inject(CLIENT_CONFIG);

  constructor() {
    this.startConnections();
  }

  private amToken: string = '';
  private hubConnection: signalR.HubConnection | null = null;

  async setB2CToken(): Promise<string> {
    const result: AuthenticationResult =
      await this.authService.instance.acquireTokenSilent({
        scopes: this.clientConfig.clientOptions.msalResourceMap[0][1],
      });
    return result.accessToken;
  }

  public startConnections(): void {
    this.amToken =
      sessionStorage.getItem(
        `${this.appConfig.digitalAccessPortal.applicationId}-applicationToken`
      ) ?? '';
    if (this.hubConnection || !this.amToken) return;

    this.stopConnection().finally(() => {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(
          `/hubs/simulationTracker?access-mgmt-apptoken=${this.amToken}`,
          {
            accessTokenFactory: () => {
              return this.setB2CToken();
            },
          }
        )
        .configureLogging(signalR.LogLevel.Debug)
        .withAutomaticReconnect()
        .build();

      this.hubConnection
        .start()
        .then(() => {
          console.debug('Connection started.');
          this.subscribeToUpdates();
          this.listenForUpdates();
        })
        .catch((reason: unknown) =>
          console.debug('Error while starting connection: ' + reason)
        );

      this.hubConnection.onreconnected(() => {
        this.subscribeToUpdates();
        this.listenForUpdates();
      });
    });
  }

  public stopConnection(): Promise<void> {
    if (this.hubConnection) {
      return this.hubConnection.stop().finally(() => {
        this.hubConnection = null;
      });
    }
    return Promise.resolve();
  }

  private subscribeToUpdates(): void {
    if (this.hubConnection) {
      this.hubConnection.send('simulationTrackerSubscribe');
    }
  }

  private listenForUpdates(): void {
    if (this.hubConnection) {
      this.hubConnection.off('simulationTrackerStatusChanged');
      this.hubConnection.on(
        'simulationTrackerStatusChanged',
        (simulationTrackerId: string) => {
          console.debug('Received update for tracker id:', simulationTrackerId);
          this.store
            .dispatch(
              new SimulationTrackerActions.GetSimulationTracker(
                simulationTrackerId
              )
            )
            .pipe(
              tap((simulationTracker: SimulationTracker) => {
                switch (simulationTracker.status) {
                  case SimulationTrackerStatus.Completed:
                    this.store.dispatch(
                      new NotificationsActions.Toast(
                        'Simulation completed successfully.',
                        Severity.Success
                      )
                    );
                    break;
                  case SimulationTrackerStatus.Failed:
                    this.store.dispatch(
                      new NotificationsActions.Toast(
                        'Simulation failed.',
                        Severity.Error
                      )
                    );
                    break;
                  default:
                    break;
                }
              })
            );
        }
      );
    }
  }
}
